/**
 * Initial state factory for the "messages" Vuex module.
 *
 * Returned as a function (rather than a plain object) so Vuex gets a fresh,
 * unshared state tree per store instantiation. Covers the conversation list
 * and its pagination/search, per-conversation message history and its
 * pagination, typing/presence and unread bookkeeping, composer drafts,
 * not-yet-started ("pending") chats, and DM-permission settings.
 */
export default () => ({
  conversations: [],
  activeConversationId: null,
  messagesByConversation: {},
  typingByConversation: {},
  // Unsent composer text per conversation — WhatsApp-style: switching away
  // from a conversation with unsent text saves it here; switching back
  // restores it into the input.
  draftsByConversation: {},
  // A conversation started from "New chat" (contact/dialed number) before
  // any message has actually been sent — kept out of `conversations` (the
  // visible chat list) so opening someone's empty thread doesn't clutter
  // the list; promoted into `conversations` the moment the first message
  // goes out. Keyed by conversationId, value is { participant }.
  pendingContacts: {},
  status: 'idle', // idle | loading | ready | error
  error: null,
  hasInitialized: false,
  // Total unread messages badge count, sourced from POST /api/messages/unread-count —
  // independent of the mocked `conversations` unreadCount fields above.
  unreadMessagesCount: 0,
  // Conversation-list pagination/search state — GET /api/messages/conversations/list.
  // The endpoint returns one homogeneous conversation_type ('user' or
  // 'group') per call, not a mixed list, so pagination is tracked per type
  // and the two streams are merged client-side (see fetchConversations in
  // store/messages/actions.js).
  conversationsSearch: '',
  conversationsSkipByType: { user: 0, group: 0 },
  conversationsLimit: 20,
  hasMoreConversationsByType: { user: true, group: true },
  isLoadingMoreConversations: false,
  // "Search People" results — GET /api/users/list-followers. Shown
  // in place of `conversations` while the search box has text in it.
  peopleSearchResults: [],
  peopleSearchOffset: 0,
  peopleSearchLimit: 20,
  hasMorePeopleSearch: true,
  isLoadingMorePeopleSearch: false,
  // True only for the first page of a search (searchPeople) — distinct from
  // isLoadingMorePeopleSearch (pagination) so the UI can suppress "No people
  // found" while the initial fetch is still in flight instead of flashing it.
  isSearchingPeople: false,
  // "Add Members" results — GET /api/users. Separate from peopleSearchResults
  // above (which is followers-only, GET /api/users/list-followers) so the
  // group member pickers (NewGroupModal, AddGroupMembersModal) can list/search
  // every user without clobbering the shared DM "Search People" state.
  allUsersResults: [],
  allUsersOffset: 0,
  allUsersLimit: 20,
  hasMoreAllUsers: true,
  isLoadingMoreAllUsers: false,
  isSearchingAllUsers: false,
  // Per-conversation message-history pagination — GET /api/messages/list.
  messagesLimit: 20,
  messagesSkipByConversation: {},
  hasMoreMessagesByConversation: {},
  isLoadingMoreMessagesByConversation: {},
  // True while the initial page of messages for the active conversation is
  // in flight (selectConversation) — distinct from isLoadingMoreMessagesByConversation
  // (scroll-up pagination) so the UI can suppress the "empty conversation"
  // placeholder instead of flashing it before the real messages arrive.
  isLoadingActiveMessages: false,
  // Snapshot of a conversation's unreadCount taken the instant it's opened,
  // before clearUnread zeroes it out — MessageList's "N new messages"
  // divider reads this to know how many trailing messages were unread,
  // since individual messages carry no read/seen flag of their own.
  unreadSnapshotByConversation: {},
  // "Who can send you Direct Messages" — 'followers' | 'everyone'. See
  // messagesApi.js's fetchMessageSettings/updateMessageSettings for why this
  // is client-persisted rather than server-fetched today.
  dmAllowFrom: 'followers',
  isDmSettingsSaving: false,
});
